import React from "react";
import { motion } from "framer-motion";
import {
    FaFacebookF,
    FaTwitter,
    FaEnvelope,
    FaQuoteLeft,
    FaGraduationCap,
    FaArrowRight,
    FaAward,
    FaBookOpen,
    FaUsers,
    FaCheckCircle,
    FaStar,
} from "react-icons/fa";

import principalImage from "./mamadoris.jpg";
import Footer from "./Footer";

const principalPageStyles = `
.jlw-principal-page {
    --jlw-navy: #071a35;
    --jlw-navy-light: #102a4c;
    --jlw-blue: #163b66;
    --jlw-gold: #d9ad55;
    --jlw-gold-light: #f0d697;
    --jlw-white: #ffffff;
    --jlw-text: #26364b;
    --jlw-muted: #68778b;
    --jlw-border: #e7eaf0;

    overflow: hidden;
    width: 100%;
    color: var(--jlw-text);
    background: #f7f8fb;
    font-family: inherit;
}

.jlw-principal-page *,
.jlw-principal-page *::before,
.jlw-principal-page *::after {
    box-sizing: border-box;
}

.jlw-principal-page a {
    text-decoration: none;
}

.jlw-principal-hero {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 310px;
    overflow: hidden;
    padding: 75px 24px 82px;
    color: var(--jlw-white);
    background:
        radial-gradient(
            circle at 85% 25%,
            rgba(217, 173, 85, 0.17),
            transparent 27%
        ),
        linear-gradient(
            120deg,
            #06152c 0%,
            #0b2343 58%,
            #163b66 100%
        );
}

.jlw-principal-hero::before {
    position: absolute;
    right: -125px;
    bottom: -260px;
    width: 520px;
    height: 520px;
    border: 1px solid rgba(240, 214, 151, 0.19);
    border-radius: 50%;
    content: "";
}

.jlw-principal-hero::after {
    position: absolute;
    right: -45px;
    bottom: -180px;
    width: 360px;
    height: 360px;
    border: 1px solid rgba(240, 214, 151, 0.14);
    border-radius: 50%;
    content: "";
}

.jlw-principal-hero-inner {
    position: relative;
    z-index: 1;
    width: min(1200px, 100%);
    margin: 0 auto;
}

.jlw-principal-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 18px;
    color: var(--jlw-gold-light);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 2.3px;
    text-transform: uppercase;
}

.jlw-principal-eyebrow::before {
    width: 32px;
    height: 2px;
    background: var(--jlw-gold);
    content: "";
}

.jlw-principal-hero h1 {
    max-width: 780px;
    margin: 0;
    color: var(--jlw-white);
    font-size: clamp(34px, 5vw, 58px);
    font-weight: 800;
    line-height: 1.13;
    letter-spacing: -1.8px;
}

.jlw-principal-hero h1 span {
    color: var(--jlw-gold-light);
}

.jlw-principal-hero-description {
    max-width: 620px;
    margin: 20px 0 0;
    color: #c0cddd;
    font-size: 15px;
    line-height: 1.9;
}

.jlw-principal-hero-rule {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 29px;
}

.jlw-principal-hero-rule span:first-child {
    width: 48px;
    height: 3px;
    background: var(--jlw-gold);
}

.jlw-principal-hero-rule span:last-child {
    width: 15px;
    height: 3px;
    background: rgba(240, 214, 151, 0.45);
}

.jlw-principal-content {
    position: relative;
    padding: 80px 24px 90px;
    background:
        radial-gradient(
            circle at 4% 30%,
            rgba(217, 173, 85, 0.055),
            transparent 23%
        ),
        #f7f8fb;
}

.jlw-principal-layout {
    display: grid;
    grid-template-columns: minmax(280px, 0.82fr) minmax(0, 1.45fr);
    align-items: start;
    gap: 48px;
    width: min(1200px, 100%);
    margin: 0 auto;
}

.jlw-principal-profile-column {
    position: relative;
    min-width: 0;
}

.jlw-principal-profile-card {
    position: relative;
    overflow: hidden;
    padding: 14px;
    background: var(--jlw-white);
    border: 1px solid var(--jlw-border);
    border-radius: 18px;
    box-shadow: 0 16px 45px rgba(7, 26, 53, 0.07);
}

.jlw-principal-photo-frame {
    position: relative;
    overflow: hidden;
    aspect-ratio: 4 / 4.7;
    background: #e8edf3;
    border-radius: 12px;
}

.jlw-principal-photo-frame::after {
    position: absolute;
    inset: 55% 0 0;
    background: linear-gradient(
        180deg,
        transparent,
        rgba(7, 26, 53, 0.38)
    );
    content: "";
    pointer-events: none;
}

.jlw-principal-photo {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    transition: transform 0.6s ease;
}

.jlw-principal-profile-card:hover .jlw-principal-photo {
    transform: scale(1.035);
}

.jlw-principal-role-badge {
    position: absolute;
    z-index: 1;
    right: 13px;
    bottom: 13px;
    left: 13px;
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 15px;
    color: var(--jlw-white);
    background: rgba(7, 26, 53, 0.92);
    border: 1px solid rgba(240, 214, 151, 0.28);
    border-radius: 10px;
    backdrop-filter: blur(10px);
}

.jlw-principal-role-icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    color: var(--jlw-navy);
    background: var(--jlw-gold);
    border-radius: 10px;
    font-size: 19px;
}

.jlw-principal-role-text span {
    display: block;
    margin-bottom: 4px;
    color: #d3deeb;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
}

.jlw-principal-role-text strong {
    display: block;
    color: var(--jlw-white);
    font-size: 14px;
    font-weight: 800;
    line-height: 1.5;
}

.jlw-principal-profile-info {
    padding: 24px 12px 12px;
    text-align: center;
}

.jlw-principal-profile-info .jlw-principal-small-label {
    display: block;
    margin-bottom: 8px;
    color: #8a6b2f;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1.8px;
    text-transform: uppercase;
}

.jlw-principal-profile-info h2 {
    margin: 0;
    color: var(--jlw-navy);
    font-size: 22px;
    font-weight: 800;
    line-height: 1.4;
}

.jlw-principal-profile-info p {
    margin: 8px 0 0;
    color: var(--jlw-muted);
    font-size: 13px;
    line-height: 1.8;
}

.jlw-principal-socials {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 20px;
}

.jlw-principal-socials a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    color: var(--jlw-blue);
    background: #f0f3f8;
    border: 1px solid #e6eaf1;
    border-radius: 9px;
    font-size: 14px;
    transition:
        color 0.2s ease,
        background 0.2s ease,
        border-color 0.2s ease,
        transform 0.2s ease;
}

.jlw-principal-socials a:hover {
    color: var(--jlw-navy);
    background: var(--jlw-gold-light);
    border-color: var(--jlw-gold);
    transform: translateY(-3px);
}

.jlw-principal-side-note {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    margin-top: 20px;
    padding: 20px;
    background: #fff;
    border: 1px solid var(--jlw-border);
    border-left: 3px solid var(--jlw-gold);
    border-radius: 10px;
}

.jlw-principal-side-note-icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 37px;
    height: 37px;
    color: #8a6b2f;
    background: #fbf4e4;
    border-radius: 9px;
    font-size: 15px;
}

.jlw-principal-side-note strong {
    display: block;
    margin-bottom: 5px;
    color: var(--jlw-navy);
    font-size: 12px;
    font-weight: 800;
}

.jlw-principal-side-note p {
    margin: 0;
    color: var(--jlw-muted);
    font-size: 12px;
    line-height: 1.8;
}

.jlw-principal-message-column {
    min-width: 0;
}

.jlw-principal-message-card {
    position: relative;
    overflow: hidden;
    padding: clamp(26px, 4vw, 47px);
    background: var(--jlw-white);
    border: 1px solid var(--jlw-border);
    border-radius: 18px;
    box-shadow: 0 16px 45px rgba(7, 26, 53, 0.055);
}

.jlw-principal-message-topline {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 23px;
}

.jlw-principal-message-topline span {
    color: #8a6b2f;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1.8px;
    text-transform: uppercase;
}

.jlw-principal-message-topline::after {
    width: 40px;
    height: 2px;
    background: var(--jlw-gold);
    content: "";
}

.jlw-principal-quote-mark {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    margin-bottom: 23px;
    color: var(--jlw-navy);
    background: #fbf2df;
    border: 1px solid #f0dfb9;
    border-radius: 13px;
    font-size: 23px;
}

.jlw-principal-message-card h2 {
    margin: 0;
    color: var(--jlw-navy);
    font-size: clamp(27px, 3.2vw, 39px);
    font-weight: 800;
    line-height: 1.25;
    letter-spacing: -0.9px;
}

.jlw-principal-message-subtitle {
    margin: 15px 0 28px;
    color: var(--jlw-muted);
    font-size: 14px;
    line-height: 1.9;
}

.jlw-principal-message-body {
    display: grid;
    gap: 19px;
}

.jlw-principal-message-body p {
    margin: 0;
    color: #536176;
    font-size: 14px;
    line-height: 2;
}

.jlw-principal-message-body p:first-child {
    color: #33445b;
    font-size: 15px;
}

.jlw-principal-message-divider {
    width: 100%;
    height: 1px;
    margin: 30px 0;
    background: linear-gradient(
        90deg,
        #e6c77f,
        #e7eaf0 65%,
        transparent
    );
}

.jlw-principal-signature {
    display: flex;
    align-items: center;
    gap: 15px;
}

.jlw-principal-signature-mark {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    color: var(--jlw-navy);
    background: var(--jlw-gold-light);
    border-radius: 50%;
    font-size: 19px;
}

.jlw-principal-signature-text strong {
    display: block;
    color: var(--jlw-navy);
    font-size: 14px;
    font-weight: 800;
}

.jlw-principal-signature-text span {
    display: block;
    margin-top: 5px;
    color: var(--jlw-muted);
    font-size: 12px;
}

.jlw-principal-message-footer {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 30px;
    padding: 16px 18px;
    color: var(--jlw-navy);
    background: #f4f6fa;
    border-radius: 9px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.7;
}

.jlw-principal-message-footer svg {
    flex-shrink: 0;
    color: #9c762e;
    font-size: 15px;
}

.jlw-principal-pillars {
    width: min(1200px, calc(100% - 48px));
    margin: 0 auto;
    padding: 0 0 82px;
}

.jlw-principal-pillars-heading {
    max-width: 620px;
    margin: 0 auto 34px;
    text-align: center;
}

.jlw-principal-pillars-heading > span {
    display: block;
    margin-bottom: 12px;
    color: #8a6b2f;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
}

.jlw-principal-pillars-heading h2 {
    margin: 0;
    color: var(--jlw-navy);
    font-size: clamp(25px, 3vw, 35px);
    font-weight: 800;
    line-height: 1.3;
    letter-spacing: -0.7px;
}

.jlw-principal-pillars-heading p {
    margin: 13px 0 0;
    color: var(--jlw-muted);
    font-size: 14px;
    line-height: 1.9;
}

.jlw-principal-pillars-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
}

.jlw-principal-pillar-card {
    padding: 28px 25px;
    background: #fff;
    border: 1px solid var(--jlw-border);
    border-radius: 13px;
    box-shadow: 0 8px 25px rgba(7, 26, 53, 0.035);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.jlw-principal-pillar-card:hover {
    box-shadow: 0 15px 35px rgba(7, 26, 53, 0.08);
    transform: translateY(-4px);
}

.jlw-principal-pillar-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 47px;
    height: 47px;
    margin-bottom: 19px;
    color: #8a6b2f;
    background: #fbf3e1;
    border: 1px solid #f0dfb9;
    border-radius: 12px;
    font-size: 19px;
}

.jlw-principal-pillar-card h3 {
    margin: 0 0 10px;
    color: var(--jlw-navy);
    font-size: 17px;
    font-weight: 800;
}

.jlw-principal-pillar-card p {
    margin: 0;
    color: var(--jlw-muted);
    font-size: 13px;
    line-height: 1.9;
}

.jlw-principal-closing {
    position: relative;
    overflow: hidden;
    padding: 58px 24px;
    color: var(--jlw-white);
    background:
        radial-gradient(
            circle at 90% 20%,
            rgba(217, 173, 85, 0.16),
            transparent 30%
        ),
        linear-gradient(120deg, #071a35, #12365f);
}

.jlw-principal-closing-inner {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 30px;
    width: min(1200px, 100%);
    margin: 0 auto;
}

.jlw-principal-closing-copy {
    max-width: 760px;
}

.jlw-principal-closing-copy > span {
    display: block;
    margin-bottom: 13px;
    color: var(--jlw-gold-light);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
}

.jlw-principal-closing-copy h2 {
    margin: 0;
    color: var(--jlw-white);
    font-size: clamp(25px, 3.5vw, 39px);
    font-weight: 800;
    line-height: 1.35;
    letter-spacing: -0.7px;
}

.jlw-principal-closing-copy p {
    max-width: 640px;
    margin: 14px 0 0;
    color: #c3d0df;
    font-size: 14px;
    line-height: 1.9;
}

.jlw-principal-closing-decoration {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 88px;
    height: 88px;
    color: var(--jlw-gold-light);
    border: 1px solid rgba(240, 214, 151, 0.42);
    border-radius: 50%;
    font-size: 35px;
}

@media (min-width: 1500px) {
    .jlw-principal-layout,
    .jlw-principal-hero-inner,
    .jlw-principal-closing-inner {
        width: min(1280px, 100%);
    }

    .jlw-principal-pillars {
        width: min(1280px, calc(100% - 64px));
    }
}

@media (max-width: 900px) {
    .jlw-principal-hero {
        min-height: 275px;
        padding: 62px 24px 68px;
    }

    .jlw-principal-content {
        padding: 55px 24px 65px;
    }

    .jlw-principal-layout {
        grid-template-columns: minmax(240px, 0.8fr) minmax(0, 1.2fr);
        gap: 25px;
    }

    .jlw-principal-profile-info h2 {
        font-size: 19px;
    }

    .jlw-principal-message-card {
        padding: 27px;
    }

    .jlw-principal-pillars {
        padding-bottom: 60px;
    }

    .jlw-principal-pillars-grid {
        gap: 14px;
    }

    .jlw-principal-pillar-card {
        padding: 23px 18px;
    }
}

@media (max-width: 680px) {
    .jlw-principal-hero {
        min-height: auto;
        padding: 55px 20px 62px;
    }

    .jlw-principal-hero h1 {
        font-size: clamp(33px, 9vw, 45px);
        letter-spacing: -1.2px;
    }

    .jlw-principal-hero-description {
        font-size: 13px;
    }

    .jlw-principal-content {
        padding: 35px 18px 52px;
    }

    .jlw-principal-layout {
        grid-template-columns: 1fr;
        gap: 25px;
    }

    .jlw-principal-profile-column {
        width: min(100%, 390px);
        margin: 0 auto;
    }

    .jlw-principal-photo-frame {
        aspect-ratio: 4 / 4.4;
    }

    .jlw-principal-profile-info h2 {
        font-size: 21px;
    }

    .jlw-principal-message-card {
        padding: 25px 21px;
        border-radius: 14px;
    }

    .jlw-principal-message-card h2 {
        font-size: 29px;
    }

    .jlw-principal-message-body {
        gap: 15px;
    }

    .jlw-principal-message-body p,
    .jlw-principal-message-body p:first-child {
        font-size: 13px;
        line-height: 1.95;
    }

    .jlw-principal-pillars {
        width: calc(100% - 36px);
        padding-bottom: 50px;
    }

    .jlw-principal-pillars-grid {
        grid-template-columns: 1fr;
        gap: 13px;
    }

    .jlw-principal-pillar-card {
        padding: 22px;
    }

    .jlw-principal-pillar-icon {
        margin-bottom: 14px;
    }

    .jlw-principal-closing {
        padding: 43px 20px;
    }

    .jlw-principal-closing-inner {
        align-items: flex-start;
        gap: 18px;
    }

    .jlw-principal-closing-copy h2 {
        font-size: 26px;
    }

    .jlw-principal-closing-copy p {
        font-size: 13px;
    }

    .jlw-principal-closing-decoration {
        width: 48px;
        height: 48px;
        font-size: 21px;
    }
}

@media (max-width: 420px) {
    .jlw-principal-eyebrow {
        font-size: 9px;
        letter-spacing: 1.6px;
    }

    .jlw-principal-hero h1 {
        font-size: 32px;
    }

    .jlw-principal-message-card {
        padding: 23px 18px;
    }

    .jlw-principal-message-card h2 {
        font-size: 26px;
    }

    .jlw-principal-closing-inner {
        flex-direction: column;
    }

    .jlw-principal-closing-decoration {
        width: 43px;
        height: 43px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .jlw-principal-page *,
    .jlw-principal-page *::before,
    .jlw-principal-page *::after {
        scroll-behavior: auto !important;
        transition-duration: 0.01ms !important;
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
    }
}
`;

const PrincipalMessagePage = () => {
    return (
        <>
            <style>{principalPageStyles}</style>

            <main className="jlw-principal-page">
                <section className="jlw-principal-hero">
                    <div className="jlw-principal-hero-inner">
                        <div className="jlw-principal-eyebrow">
                            Leadership &amp; Vision
                        </div>

                        <h1>
                            A Message From
                            <br />
                            <span>Our Principal</span>
                        </h1>

                        <p className="jlw-principal-hero-description">
                            A commitment to meaningful education, strong
                            character, personal growth, and opportunities
                            that help individuals and communities thrive.
                        </p>

                        <div
                            className="jlw-principal-hero-rule"
                            aria-hidden="true"
                        >
                            <span />
                            <span />
                        </div>
                    </div>
                </section>

                <section className="jlw-principal-content">
                    <div className="jlw-principal-layout">
                        <motion.aside
                            className="jlw-principal-profile-column"
                            initial={{ opacity: 0, x: -35 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.65 }}
                            viewport={{ once: true, amount: 0.15 }}
                        >
                            <div className="jlw-principal-profile-card">
                                <div className="jlw-principal-photo-frame">
                                    <img
                                        src={principalImage}
                                        alt="Principal of the Jana L. Williams Educational Foundation"
                                        className="jlw-principal-photo"
                                        loading="eager"
                                    />

                                    <div className="jlw-principal-role-badge">
                                        <div className="jlw-principal-role-icon">
                                            <FaGraduationCap />
                                        </div>

                                        <div className="jlw-principal-role-text">
                                            <span>Leadership</span>
                                            <strong>
                                                Principal &amp; Educational
                                                Leader
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                <div className="jlw-principal-profile-info">
                                    <span className="jlw-principal-small-label">
                                        A Commitment to Education
                                    </span>

                                    <h2>
                                        Message From Our Principal
                                    </h2>

                                    <p>
                                        Guiding learners through knowledge,
                                        values, and opportunities for growth.
                                    </p>

                                    <div className="jlw-principal-socials">
                                        <a
                                            href="https://www.facebook.com/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="Facebook"
                                        >
                                            <FaFacebookF />
                                        </a>

                                        <a
                                            href="mailto:"
                                            aria-label="Email the foundation"
                                        >
                                            <FaEnvelope />
                                        </a>

                                        <a
                                            href="https://x.com/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="X"
                                        >
                                            <FaTwitter />
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="jlw-principal-side-note">
                                <div className="jlw-principal-side-note-icon">
                                    <FaAward />
                                </div>

                                <div>
                                    <strong>Our Educational Commitment</strong>
                                    <p>
                                        Supporting learning, character
                                        development, leadership, and
                                        meaningful opportunity.
                                    </p>
                                </div>
                            </div>
                        </motion.aside>

                        <motion.article
                            className="jlw-principal-message-column"
                            initial={{ opacity: 0, x: 35 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.65, delay: 0.1 }}
                            viewport={{ once: true, amount: 0.15 }}
                        >
                            <div className="jlw-principal-message-card">
                                <div className="jlw-principal-message-topline">
                                    <span>A Word of Welcome</span>
                                </div>

                                <div className="jlw-principal-quote-mark">
                                    <FaQuoteLeft />
                                </div>

                                <h2>
                                    Education Opens Doors to a Brighter Future
                                </h2>

                                <p className="jlw-principal-message-subtitle">
                                    Welcome to the Jana L. Williams
                                    Educational Foundation. We believe that
                                    education can create opportunity, nurture
                                    potential, and strengthen communities.
                                </p>

                                <div className="jlw-principal-message-body">
                                    <p>
                                        It is a privilege to welcome you to
                                        our foundation. Education is more
                                        than the acquisition of knowledge;
                                        it is a pathway to personal growth,
                                        responsible leadership, and a future
                                        filled with possibility.
                                    </p>

                                    <p>
                                        We believe every learner deserves
                                        the opportunity to discover their
                                        strengths, develop practical skills,
                                        and build the confidence to pursue
                                        meaningful goals. Our commitment is
                                        to encourage learning, inspire
                                        ambition, and support the development
                                        of individuals who can make a
                                        positive difference in society.
                                    </p>

                                    <p>
                                        Through education, character
                                        development, leadership, and
                                        community engagement, we seek to
                                        encourage a culture of responsibility,
                                        integrity, creativity, and service.
                                        We recognize that lasting progress
                                        comes when individuals, families,
                                        educators, and communities work
                                        together toward shared goals.
                                    </p>

                                    <p>
                                        I invite students, parents,
                                        educators, partners, and community
                                        members to join us in advancing
                                        educational opportunity and
                                        empowering the next generation.
                                        Together, we can help create an
                                        environment where potential is
                                        recognized, talent is nurtured, and
                                        brighter futures become possible.
                                    </p>

                                    <p>
                                        Thank you for your interest in the
                                        Jana L. Williams Educational
                                        Foundation. Your encouragement,
                                        partnership, and commitment to
                                        education are valuable as we work
                                        toward a more promising future.
                                    </p>
                                </div>

                                <div className="jlw-principal-message-divider" />

                                <div className="jlw-principal-signature">
                                    <div className="jlw-principal-signature-mark">
                                        <FaGraduationCap />
                                    </div>

                                    <div className="jlw-principal-signature-text">
                                        <strong>
                                            With sincere appreciation
                                        </strong>
                                        <span>
                                            Principal
                                            <br />
                                            Jana L. Williams Educational
                                            Foundation
                                        </span>
                                    </div>
                                </div>

                                <div className="jlw-principal-message-footer">
                                    <FaCheckCircle />
                                    <span>
                                        Learning, leadership, integrity, and
                                        opportunity for a brighter future.
                                    </span>
                                </div>
                            </div>
                        </motion.article>
                    </div>
                </section>

                <section className="jlw-principal-pillars">
                    <div className="jlw-principal-pillars-heading">
                        <span>Our Guiding Principles</span>

                        <h2>
                            The Values Behind Our Vision
                        </h2>

                        <p>
                            We believe lasting progress begins with
                            knowledge, grows through leadership, and
                            becomes meaningful through service to others.
                        </p>
                    </div>

                    <div className="jlw-principal-pillars-grid">
                        <motion.article
                            className="jlw-principal-pillar-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45 }}
                            viewport={{ once: true, amount: 0.2 }}
                        >
                            <div className="jlw-principal-pillar-icon">
                                <FaBookOpen />
                            </div>

                            <h3>Quality Education</h3>

                            <p>
                                Encouraging continuous learning, knowledge,
                                practical skills, and the confidence to
                                pursue new opportunities.
                            </p>
                        </motion.article>

                        <motion.article
                            className="jlw-principal-pillar-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45, delay: 0.1 }}
                            viewport={{ once: true, amount: 0.2 }}
                        >
                            <div className="jlw-principal-pillar-icon">
                                <FaGraduationCap />
                            </div>

                            <h3>Leadership &amp; Character</h3>

                            <p>
                                Promoting integrity, responsibility,
                                discipline, and the ability to lead with
                                purpose and compassion.
                            </p>
                        </motion.article>

                        <motion.article
                            className="jlw-principal-pillar-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45, delay: 0.2 }}
                            viewport={{ once: true, amount: 0.2 }}
                        >
                            <div className="jlw-principal-pillar-icon">
                                <FaUsers />
                            </div>

                            <h3>Community Empowerment</h3>

                            <p>
                                Supporting collaboration and opportunity
                                so individuals and communities can work
                                toward a stronger future.
                            </p>
                        </motion.article>
                    </div>
                </section>

                <section className="jlw-principal-closing">
                    <div className="jlw-principal-closing-inner">
                        <div className="jlw-principal-closing-copy">
                            <span>Moving Forward Together</span>

                            <h2>
                                Every Learner Deserves the Chance to Succeed.
                            </h2>

                            <p>
                                Together, we can encourage learning, nurture
                                potential, and help build a future shaped
                                by knowledge, opportunity, and service.
                            </p>
                        </div>

                        <div
                            className="jlw-principal-closing-decoration"
                            aria-hidden="true"
                        >
                            <FaStar />
                        </div>
                    </div>
                </section>

                <Footer />
            </main>
        </>
    );
};

export default PrincipalMessagePage;